export const features = [
    {
        icon: "dashboard",
        title: "Real-time booking dashboard",
        description:
            "See all upcoming reservations at a glance. Filter by date, status, or party size.",
    },
    {
        icon: "three_d_rotation",
        title: "Live 3D floor plan",
        description:
            "Rotate a 3D floor plan to see which tables are seated, free or on a tight turn. Slide the clock forward to preview the evening, and drag a booking to another table to move it.",
    },
    {
        icon: "notifications_active",
        title: "Instant alerts when a booking comes in",
        description:
            "Your dashboard pings and plays a sound the moment a new reservation lands — no need to keep refreshing during service.",
    },
    {
        icon: "mark_email_read",
        title: "Automatic email notifications",
        description: "Customers get a confirmation email as soon as they book.",
    },
    {
        icon: "table_restaurant",
        title: "Automatic table assignment",
        description:
            "When a booking comes in, DiningMate finds the best table combination automatically — no manual intervention needed.",
    },
    {
        icon: "call_merge",
        title: "Connect tables for bigger parties",
        description:
            "Need a table for 8 but only have four-tops? Combine adjacent tables into one reservation — DiningMate tracks the combined capacity so nothing gets double-booked.",
    },
    {
        icon: "record_voice_over",
        title: "Voice & natural language booking",
        description:
            "Customers say or type 'Saturday night for 4' — DiningMate handles the rest. No forms.",
    },
    {
        icon: "credit_card",
        title: "No-show protection with card on file",
        description:
            "Guests save a card when they book. If they cancel late or don't show up, you can charge a fee — DiningMate takes no cut of it.",
    },
    {
        icon: "insights",
        title: "Busy-hours heatmap",
        description:
            "See exactly which days and hours fill up fastest over the last 30 days to a year, so you can staff smarter and plan promotions around your slow periods.",
    },
    {
        icon: "devices",
        title: "Works on any device",
        description:
            "Manage bookings from your phone, tablet, or computer. No app download required.",
    },
    {
        icon: "storefront",
        title: "Manage multiple locations, one login",
        description:
            "Running more than one venue? Switch between locations instantly from a single dashboard — no separate logins needed.",
    },
    {
        icon: "manage_search",
        title: "Guest search across all locations",
        description:
            "Look up a guest's booking history no matter which of your locations they visited.",
    },
];

export const contactBenefits = [
    "Direct founder support",
    "Same-day onboarding",
    "Custom feature requests",
    "No obligation",
    "Local Aussie team",
    "No lock-in contract",
];

export const pricings = [
    {
        title: "All Included",
        subtitle: "Everything your restaurant needs. Nothing you don't.",
        price: "$49",
        priceSuffix: " / month",
        features: [
            "Unlimited bookings",
            "Natural language booking (type or voice)",
            "Real-time booking dashboard",
            "Email notifications",
            "Optional no-show & late cancellation fees",
            "No per-booking fees",
            "No lock-in contract",
            "Onboarding support from the founder",
            "Australian-hosted, Australian support",
        ],
        disclaimer: "3-month free trial included. Prices in AUD. No setup fee.",
        isHighlighted: true,
    },
];

export const faq = [
    {
        id: "faq-1",
        title: "Is it hard to set up?",
        text: "No. Most restaurants are set up and taking bookings within 10–15 minutes. A short onboarding flow walks you through each step — no help docs, no guesswork. If you get stuck at any point, just message us directly.",
    },
    {
        id: "faq-2",
        title: "Can customers book by voice?",
        text: 'Yes. Customers tap a mic icon and say something like "Table for 2 on Friday evening." DiningMate picks up the time, date, and party size and shows available slots. Works on any phone or computer — nothing to download.',
    },
    {
        id: "faq-3",
        title: "Can customers book by typing normally too?",
        text: 'Yes. They can type something like "Saturday lunch for 3" and DiningMate will interpret it and show available times. It also works with standard form-style booking if customers prefer that.',
    },
    {
        id: "faq-4",
        title: "Do I need any technical knowledge?",
        text: "No. If you can use a smartphone, you can use DiningMate. There is no code to write and no technical setup required.",
    },
    {
        id: "faq-5",
        title: "Are there any per-booking fees?",
        text: "No. DiningMate is $49/month flat. You can take as many bookings as you want — there is no additional charge per reservation, ever.",
    },
    {
        id: "faq-5b",
        title: "Are bookings really unlimited?",
        text: "Yes. There is no booking count cap and no per-booking charge — your $49/month covers every reservation your restaurant takes.\n\nWe do apply a fair use policy, which lets us review accounts showing automated abuse, activity that is clearly not a restaurant operation, or platform load that is unusually excessive compared to a normal venue. In practice, if you are running a restaurant and customers are booking tables, this will never affect you.",
    },
    {
        id: "faq-6",
        title: "Is this suitable for a small restaurant?",
        text: "Yes — it was built specifically for small and medium restaurants. It is not designed for hotel chains or large venue groups. If you have one venue and want something simple and affordable, this is for you.",
    },
    {
        id: "faq-6b",
        title: "Is there a free trial?",
        text: "Yes. Every new account gets a 3-month free trial — no credit card required to start. You can set up your restaurant, share your booking link, and take real bookings during the trial. If it's not for you, just don't add a payment method and the account closes automatically.",
    },
    {
        id: "faq-6c",
        title: "Can I charge guests for no-shows or late cancellations?",
        text: "Yes — it's optional. Turn it on and guests save a card when they book. If they cancel late or don't show up, you can charge a fee straight to that card. It's your fee, paid into your own bank account — DiningMate doesn't take a cut of it.",
    },
    {
        id: "faq-7",
        title: "Can I cancel anytime?",
        text: "Yes. There is no annual contract. You pay monthly and can cancel at any time with no cancellation fee.",
    },
    {
        id: "faq-8",
        title: "What if I have a problem or question?",
        text: "You contact the founder directly. DiningMate is a small, founder-led product — you're not going through a support queue. We typically respond the same day during business hours.",
    },
];

export const problemPoints = [
    "Answering the same calls during service to take a name, time, and table size",
    "Customers messaging on Instagram at 11pm asking about Saturday availability",
    "Bookings spread across a notebook, a spreadsheet, or someone's phone",
    "Double bookings — and no-shows that cost you a table you could've filled",
    "Booking software that was too expensive, too complicated, or charged per booking on top of a monthly fee",
];

export const howItWorksSteps = [
    {
        number: "1",
        title: "Share your booking link",
        description:
            "You get a simple booking page linked to your restaurant. Share it on your website, Instagram bio, or Google profile.",
    },
    {
        number: "2",
        title: "Customers type or speak naturally",
        description:
            'They can type "Table for 2 tomorrow evening" — or tap the mic and say it out loud. DiningMate picks up the date, time, and party size automatically. No confusing forms.',
    },
    {
        number: "3",
        title: "They pick a time and confirm",
        description:
            "Available slots are shown instantly. They pick a time, enter their name and contact, and they're booked.",
    },
    {
        number: "4",
        title: "You see it in your dashboard",
        description:
            "The booking appears immediately. You get a notification. No double bookings. No missed reservations.",
    },
];

export const comparisonRows = [
    { label: "Monthly price", diningMate: "$49/month flat", typical: "$50–$300+/month" },
    { label: "Per-booking fee", diningMate: "None", typical: "$1–$3 per booking" },
    { label: "Setup time", diningMate: "10–15 minutes", typical: "Days to weeks" },
    { label: "Contract", diningMate: "No lock-in", typical: "Annual contracts common" },
    { label: "Support", diningMate: "Direct from founder", typical: "Ticket queue / chatbot" },
    { label: "Built for small restaurants", diningMate: "Yes", typical: "Often overkill" },
    { label: "Voice / natural language booking", diningMate: "Yes", typical: "Rarely" },
];

export const foundingBenefits = [
    "Locked-in rate of $39/month — forever, not just an intro offer",
    "Direct access to the founder for setup and feedback",
    "Feature requests go to the top of the roadmap",
    "Early access to new features before general release",
    "Recognition on the DiningMate website (optional)",
];

export const reviews = [
    {
        authorPhoto: {
            src: "https://assets.maccarianagency.com/the-front/photos/people/veronica-adams.jpg",
            srcSet: "https://assets.maccarianagency.com/the-front/photos/people/veronica-adams@2x.jpg 2x",
        },
        authorName: "Veronica Adams",
        authorOccupation: "Growth Marketer, Crealytics",
        feedback:
            "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
    },
    {
        authorPhoto: {
            src: "https://assets.maccarianagency.com/the-front/photos/people/akachi-luccini.jpg",
            srcSet: "https://assets.maccarianagency.com/the-front/photos/people/akachi-luccini@2x.jpg 2x",
        },
        authorName: "Akachi Luccini",
        authorOccupation: "Lead Generation, Alternative Capital",
        feedback:
            "Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.",
    },
    {
        authorPhoto: {
            src: "https://assets.maccarianagency.com/the-front/photos/people/jack-smith.jpg",
            srcSet: "https://assets.maccarianagency.com/the-front/photos/people/jack-smith@2x.jpg 2x",
        },
        authorName: "Jack Smith",
        authorOccupation: "Head of Operations, Parkfield Commerce",
        feedback:
            "Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.",
    },
];
